import {
  Smartphone,
  Cable,
  Cpu,
  Gauge,
  ShieldCheck,
  Video,
  MonitorPlay,
  Speaker,
  Zap,
  Wrench,
  Tv,
  Sparkles,
  LayoutDashboard,
  Camera,
  MapPin,
  Cast,
  Plug,
  Stethoscope,
  Battery,
  BatteryCharging,
  BatteryFull,
  Power,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES, IMAGES, BUSINESS } from "../data";

const ICONS = {
  Smartphone,
  Cable,
  Cpu,
  Gauge,
  ShieldCheck,
  Video,
  MonitorPlay,
  Speaker,
  Zap,
  Wrench,
  Tv,
  Sparkles,
  LayoutDashboard,
  Camera,
  MapPin,
  Cast,
  Plug,
  Stethoscope,
  Battery,
  BatteryCharging,
  BatteryFull,
  Power,
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#0A0C10]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#00F0FF] mb-5">
              / Services · {SERVICES.length} specialisms
            </div>
            <h2
              data-testid="services-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-white tracking-tighter leading-[1.05]"
            >
              Every service your car's{" "}
              <span className="text-gradient-cyan">electronics</span> will ever
              need.
            </h2>
          </div>
          <p className="max-w-md text-[#9CA3AF] text-base leading-relaxed">
            From software coding and security to fabricated wiring, fuse boxes
            and roadside jump-starts — executed with the patience of an OEM
            technician and the speed of a mobile specialist.
          </p>
        </div>

        {/* Featured banner */}
        <div className="mb-6">
          <FeatureImageCard
            image={IMAGES.ambient}
            overline="Signature work"
            title="Ambient Lighting & Starlight Headliners"
            desc="Fibre-optic headliners, multi-zone ambient, door LEDs — tuned, diffused and installed to OEM standard."
            testId="service-card-ambient-feature"
          />
        </div>

        {/* Uniform grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
          data-testid="services-grid"
        >
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} data={s} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/5 pt-10">
          <p className="text-white/70 max-w-lg">
            Job you can't see here? We love the challenges other shops turn
            down. Message us and we'll tell you what's possible.
          </p>
          <a
            href={BUSINESS.whatsappText}
            target="_blank"
            rel="noreferrer"
            data-testid="services-cta-whatsapp"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium px-5 py-3 rounded-sm transition-colors"
          >
            Ask about your build
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ data }) {
  const Icon = ICONS[data.icon] || Sparkles;
  const hasImage = !!data.image;
  return (
    <article
      data-testid={`service-card-${data.id}`}
      className="card-glow group relative bg-[#12141C] border border-white/5 rounded-sm overflow-hidden flex flex-col"
    >
      {hasImage ? (
        <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0C10]">
          <img
            src={data.image}
            alt={data.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12141C]/60 via-transparent to-transparent" />
          <div className="absolute top-3 left-3 h-8 w-8 rounded-sm bg-[#0A0C10]/70 backdrop-blur-sm border border-white/10 grid place-items-center">
            <Icon className="h-4 w-4 text-[#00F0FF]" strokeWidth={1.5} />
          </div>
        </div>
      ) : (
        <div className="px-6 pt-6 sm:px-7 sm:pt-7">
          <div className="h-10 w-10 rounded-sm bg-[#00F0FF]/10 border border-[#00F0FF]/20 grid place-items-center">
            <Icon className="h-5 w-5 text-[#00F0FF]" strokeWidth={1.5} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col p-6 sm:p-7">
        <h3 className="font-heading text-lg sm:text-xl font-medium text-white tracking-tight leading-tight">
          {data.title}
        </h3>
        <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed flex-1">
          {data.desc}
        </p>
        <div className="mt-6 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/40 group-hover:text-[#00F0FF] transition-colors">
          Enquire
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </article>
  );
}

function FeatureImageCard({ image, overline, title, desc, testId }) {
  return (
    <article
      data-testid={testId}
      className="card-glow relative overflow-hidden rounded-sm border border-white/5 bg-[#12141C] aspect-[16/9] sm:aspect-[21/9] lg:aspect-[24/9]"
    >
      <img
        src={image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0C10] via-[#0A0C10]/60 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-10">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#00F0FF] mb-3">
          {overline}
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white font-medium tracking-tight max-w-xl">
          {title}
        </h3>
        <p className="mt-3 text-sm text-white/70 max-w-xl leading-relaxed">
          {desc}
        </p>
      </div>
    </article>
  );
}
